/**
 * Donations Router Contract — Operation IDs + Type Maps
 *
 * Defines the typed operation ID arrays and Args/Result type maps for
 * the donations domain router transport. These are consumed by the
 * transport registry and domain router client for type-safe calls.
 *
 * Pattern: packages/domain-core/src/entities/payments-router-contract.ts
 */

import type {
  CampaignCalendarResponse,
  DonationCampaign,
  DonationCampaignListResponse,
  DonationCampaignType,
  FamilyDonationHistoryListResponse,
  PendingDedicationsListResponse,
  DailyHonoreesResponse,
  LedgerListResponse,
  TenantParnasHayomConfig,
  Donation,
} from '../contracts/index';
import type { DomainRouterJsonInput } from './domain-router-transport';

// ---------------------------------------------------------------------------
// Operation ID Arrays
// ---------------------------------------------------------------------------

export const DONATIONS_QUERY_OPERATION_IDS = [
  'getDonationsCampaignCalendar',
  'getDonationsTenantConfig',
  'listDonationsFamilyHistory',
  'listDonationsPendingDedications',
  'getDonationsDailyHonorees',
  'getDonationsLedger',
  'getDonationsCampaignList',
  'getDonationsCampaignDetail',
] as const;

export const DONATIONS_MUTATION_OPERATION_IDS = [
  'createDonationsCampaign',
  'updateDonationsCampaign',
  'approveDonationsDedication',
  'rejectDonationsDedication',
  'createDonationsOfflineDonation',
  'issueDonationsRefund',
  'resendDonationsReceipt',
  'updateDonationsTenantConfig',
] as const;

export type DonationsQueryOperationId = (typeof DONATIONS_QUERY_OPERATION_IDS)[number];
export type DonationsMutationOperationId = (typeof DONATIONS_MUTATION_OPERATION_IDS)[number];

// ---------------------------------------------------------------------------
// Query Args / Result Maps
// ---------------------------------------------------------------------------

export type DonationsQueryArgsMap = {
  getDonationsCampaignCalendar: { input?: { campaignId?: string; campaignType?: DonationCampaignType; year?: number; month?: number } | DomainRouterJsonInput };
  getDonationsTenantConfig: { input?: DomainRouterJsonInput };
  listDonationsFamilyHistory: { input?: { limit?: number; nextToken?: string; campaignType?: string } | DomainRouterJsonInput };
  listDonationsPendingDedications: { input?: { limit?: number; nextToken?: string; campaignId?: string } | DomainRouterJsonInput };
  getDonationsDailyHonorees: { input?: { date?: string } | DomainRouterJsonInput };
  getDonationsLedger: { input?: { limit?: number; nextToken?: string; status?: string; campaignType?: string; startDate?: string; endDate?: string } | DomainRouterJsonInput };
  getDonationsCampaignList: { input?: { limit?: number; nextToken?: string; isActive?: boolean } | DomainRouterJsonInput };
  getDonationsCampaignDetail: { input?: { campaignId: string } | DomainRouterJsonInput };
};

export type DonationsQueryResultMap = {
  getDonationsCampaignCalendar: CampaignCalendarResponse;
  getDonationsTenantConfig: TenantParnasHayomConfig;
  listDonationsFamilyHistory: FamilyDonationHistoryListResponse;
  listDonationsPendingDedications: PendingDedicationsListResponse;
  getDonationsDailyHonorees: DailyHonoreesResponse;
  getDonationsLedger: LedgerListResponse;
  getDonationsCampaignList: DonationCampaignListResponse;
  getDonationsCampaignDetail: DonationCampaign;
};

// ---------------------------------------------------------------------------
// Mutation Args / Result Maps
// ---------------------------------------------------------------------------

export type DonationsMutationArgsMap = {
  createDonationsCampaign: { input: Partial<DonationCampaign> | DomainRouterJsonInput };
  updateDonationsCampaign: { input: { campaignId: string } & Partial<DonationCampaign> | DomainRouterJsonInput };
  approveDonationsDedication: { input: { donationId: string } | DomainRouterJsonInput };
  rejectDonationsDedication: { input: { donationId: string; reason?: string } | DomainRouterJsonInput };
  createDonationsOfflineDonation: { input: Partial<Donation> | DomainRouterJsonInput };
  issueDonationsRefund: { input: { donationId: string; reason?: string } | DomainRouterJsonInput };
  resendDonationsReceipt: { input: { donationId: string } | DomainRouterJsonInput };
  updateDonationsTenantConfig: { input: Partial<TenantParnasHayomConfig> | DomainRouterJsonInput };
};

export type DonationsMutationResultMap = {
  createDonationsCampaign: DonationCampaign;
  updateDonationsCampaign: DonationCampaign;
  approveDonationsDedication: Donation;
  rejectDonationsDedication: Donation;
  createDonationsOfflineDonation: Donation;
  issueDonationsRefund: Donation;
  resendDonationsReceipt: { success: boolean };
  updateDonationsTenantConfig: TenantParnasHayomConfig;
};
